import { Dispatch, RefObject, SetStateAction, useCallback } from "react";

import { Box, SxProps, Theme } from "@mui/material";
import Grid from "@mui/material/Grid";
import ReactCrop, { Crop as CropState, PixelCrop } from "react-image-crop";

import { AmplitudeEvents } from "../../../common/constants/amplitude";

export interface CroppedFile {
  url: string;
  type: string;
}

export interface CropProps {
  croppedFile?: CroppedFile;
  ratio: number | undefined;
  rotate: number;
  handleAmplitudeEvent: (event: AmplitudeEvents) => void;
  completedCrop: PixelCrop | undefined;
  setCompletedCrop: Dispatch<SetStateAction<PixelCrop | undefined>>;
  imgRef: RefObject<HTMLImageElement | null>;
  previewCanvasRef: RefObject<HTMLCanvasElement | null>;
  setImageCroppedByUser: Dispatch<SetStateAction<boolean>>;
  onImageLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  crop: CropState | undefined;
  setCrop: Dispatch<SetStateAction<CropState | undefined>>;
}

const MIN_WIDTH = 10;
const MIN_HEIGHT = 10;

const reactCropSx: SxProps<Theme> = {
  maxWidth: "100%",
  "& .ReactCrop__child-wrapper": {
    height: "100%",
    maxWidth: "100%",
    display: "flex",
    justifyContent: "center",
  },
};

export const Crop = ({
  croppedFile,
  ratio,
  rotate,
  handleAmplitudeEvent,
  completedCrop,
  setCompletedCrop,
  previewCanvasRef,
  imgRef,
  setImageCroppedByUser,
  onImageLoad,
  crop,
  setCrop,
}: CropProps) => {
  const handleCrop = useCallback(
    (crop: CropState) => {
      setCrop(crop);
    },
    [setCrop]
  );
  const handleCompleteCrop = useCallback(
    (crop: PixelCrop) => {
      setCompletedCrop(crop);
    },
    [setCompletedCrop]
  );

  const handleClick = useCallback(() => {
    setImageCroppedByUser(true);
    handleAmplitudeEvent(AmplitudeEvents.ImageUploaderImageCropped);
  }, [handleAmplitudeEvent, setImageCroppedByUser]);

  return (
    <>
      {croppedFile && (
        <Grid
          item
          xs={12}
          onClick={handleClick}
          sx={{
            display: "flex",
            justifyContent: "center",
            height: "100%",
            position: "relative",
          }}
        >
          <Box sx={reactCropSx}>
            <ReactCrop
              crop={crop}
              onChange={handleCrop}
              aspect={ratio}
              onComplete={handleCompleteCrop}
              minWidth={MIN_WIDTH}
              minHeight={MIN_HEIGHT}
              style={{ height: "100%" }}
            >
              <img
                alt="Cropped-image"
                src={croppedFile.url}
                ref={imgRef}
                onLoad={onImageLoad}
                style={{ transform: `rotate(${rotate}deg)` }}
                crossOrigin="anonymous"
                height="100%"
                width={undefined}
              />
            </ReactCrop>
          </Box>
        </Grid>
      )}
      {completedCrop && (
        <canvas
          id="crop"
          ref={previewCanvasRef}
          style={{
            border: "1px solid black",
            objectFit: "contain",
            width: completedCrop.width,
            height: completedCrop.height,
            maskRepeat: "no-repeat",
            display: "none",
          }}
        />
      )}
    </>
  );
};
